import Box from "@mui/material/Box";
import type {DialogProps} from "@mui/material/Dialog";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import {useQuery} from "react-query";
import type {ReactElement} from "react";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import type {SyncTask} from "crqlar-react-common/dist/models/integrations/syncTask.model";

interface SyncTaskPreviewDialogProps extends DialogProps {
    syncTaskId?: string
    queryScope: string
    getTask: (id: string) => Promise<SyncTask | null | undefined>
    additionalInfo?: (task?: SyncTask) => ReactElement | null
}

const SyncTaskPreviewDialog = (props: SyncTaskPreviewDialogProps) => {
    const {
        syncTaskId,
        queryScope,
        getTask,
        additionalInfo,
        open,
        onClose,
        ...dialogProps
    } = props;
    const {t} = useTranslation();

    const syncTaskQuery = useQuery(['syncTask', queryScope, syncTaskId], () => {
        if (syncTaskId) {
            return getTask(syncTaskId);
        }
        return Promise.resolve(null);
    }, {
        enabled: open && !!syncTaskId,
        refetchInterval: 5 * 1000
    });
    const syncTask = syncTaskQuery.data;

    let uiContent = null;
    if (syncTaskQuery.isLoading) {
        uiContent = <div>{t('Loading')}</div>;
    } else if (syncTaskQuery.isError) {
        uiContent = <div>{t('Error')}</div>;
    } else if (!syncTask) {
        uiContent = <div>{t('No data')}</div>;
    } else {
        uiContent = (
            <Stack gap={1}>

                <Stack direction={'row'} gap={1}>
                    <Typography sx={{minWidth: 150}}>
                        {t('Type')}:
                    </Typography>
                    <Typography sx={{fontWeight: 'bold'}}>
                        {syncTask.syncType}
                    </Typography>
                </Stack>

                <Stack direction={'row'} gap={1}>
                    <Typography sx={{minWidth: 150}}>
                        {t('Status')}:
                    </Typography>
                    <Typography sx={{fontWeight: 'bold'}}>
                        {syncTask.status}
                    </Typography>
                </Stack>

                <Stack direction={'row'} gap={1}>
                    <Typography sx={{minWidth: 150}}>
                        {t('Processing start')}:
                    </Typography>
                    <Typography sx={{fontWeight: 'bold'}}>
                        {formatDateDMYHM(syncTask.processingStart)}
                    </Typography>
                </Stack>

                <Stack direction={'row'} gap={1}>
                    <Typography sx={{minWidth: 150}}>
                        {t('Processing done')}:
                    </Typography>
                    <Typography sx={{fontWeight: 'bold'}}>
                        {formatDateDMYHM(syncTask.processingEnd)}
                    </Typography>
                </Stack>

                {
                    additionalInfo?.(syncTask)
                }

                <Stack gap={0.5}>
                    <Typography sx={{minWidth: 150}}>
                        {t('Trace')}:
                    </Typography>
                    <Box
                        sx={{
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.8rem',
                            border: '1px solid #ccc',
                            padding: 1
                        }}
                    >
                        {syncTaskQuery.data?.message}
                    </Box>
                </Stack>

            </Stack>
        )
    }

    return (
        <Dialog
            {...dialogProps}
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={'lg'}
        >
            <DialogContent>
                {uiContent}
            </DialogContent>
        </Dialog>
    )

}

export default SyncTaskPreviewDialog;
